import { Trans, useTranslation } from "react-i18next";
import { Picture } from "~/components/ui/picture";
import { SectionTitle } from "~/components/ui/section-title";
import { useObdCompatibilityChecker } from "~/components/products/pdp/ObdCompatibilityCheckerContext";
import { imageUrl } from "~/lib/image-url";

// Plain public/ string path, not a JS import — see the comment on bgImageSet in
// app/components/home/UseCases.tsx for why.
const obdCompatibilityBg = imageUrl("/images/products/compatibility/compatibility-bg.png");

// ObdCompatibilityCheckerProvider and the drawer are mounted in root.tsx, not here — one
// shared state and one drawer for every compatibility checker trigger on the site.
export function ObdCompatibilityCheckerSection() {
  const { t } = useTranslation("products");
  const { setOpen } = useObdCompatibilityChecker();

  return (
    <>
      {/* One tree, not three sections — mobile/tablet/desktop differ only via `md:`/`lg:` resets. */}
      <section id="compatibility" className="fleet:bg-background relative h-[318px] w-full scroll-mt-20 overflow-hidden bg-[#f0f2f6] md:h-[361px] md:bg-[#e8eaee]">
        <Picture
          aria-hidden
          src={obdCompatibilityBg}
          alt=""
          // Fixed size/offset per Figma's 375/768/1440 frames (tablet has its own crop,
          // not a scaled desktop one) — width%/height:auto zoomed in further on wide screens.
          // lg:fleet: — the fleet desktop frame crops wider than consumer's (no fleet tablet frame).
          className="lg:fleet:top-[-622px] lg:fleet:left-[-500px] lg:fleet:h-[1455px] lg:fleet:w-[1940px] absolute top-[-155.15px] left-[calc(50%-398.14px)] h-[607.57px] w-[811px] max-w-none md:top-[-345px] md:left-[-364px] md:h-[975px] md:w-[1300px] lg:top-[-573px] lg:left-[-433px] lg:h-[1367px] lg:w-[1823px]"
        />

        {/* Fleet PDP (Figma 10480:408135): the same photo dimmed to 50% Bg-Primary. */}
        <div aria-hidden className="fleet:block pointer-events-none absolute inset-0 hidden bg-[rgba(22,26,33,0.5)]" />

        {/* Vignette: vertical fade on mobile, left-right fade on desktop. 3rd mobile
            layer fades the crop box's left edge, visible past ~620px card width. */}
        <div
          aria-hidden
          className="fleet:bg-[linear-gradient(180deg,rgba(22,26,33,0.85)_9.13%,rgba(22,26,33,0.425)_29.8%,rgba(31,42,63,0)_50.46%),linear-gradient(180deg,rgba(22,26,33,0)_61.31%,rgba(22,26,33,0.2)_72.17%,rgba(22,26,33,0.8)_84.95%,#161a21_99.96%)] md:fleet:bg-[linear-gradient(270deg,rgba(22,26,33,0.85)_33.06%,rgba(31,42,63,0)_59.38%)] pointer-events-none absolute inset-0 bg-[linear-gradient(179.82deg,#f0f2f6_20%,rgba(240,242,246,0)_35.3%),linear-gradient(178.48deg,rgba(240,242,246,0)_68.11%,#e8eaee_82.86%),linear-gradient(to_right,#f0f2f6_0%,rgba(240,242,246,0)_22%)] md:bg-[linear-gradient(to_right,rgba(232,234,238,0)_36.8%,#e8eaee_61.8%)]"
        />

        <div className="wrap flex flex-col items-center py-10 text-center md:items-end md:pt-[79px] md:pb-[80px] md:text-right">
          {/* Single positioned block, not three independently-offset absolute elements —
              the old per-element pixel offsets assumed a one-line description and a
              standalone CTA button below it. Both are gone (the CTA is now the inline
              "compatible" link below), so the mobile copy flows in one column instead of
              being pinned to hand-tuned coordinates that would overlap with longer text.
              On mobile it spans the card (top/bottom 32px) and mt-auto drops the
              description to the bottom, per Figma. */}
          <div className="xl:fleet:mr-[61px] absolute top-8 left-1/2 flex w-[317px] -translate-x-1/2 flex-col gap-3 max-md:bottom-8 max-md:gap-2 md:static md:top-auto md:left-auto md:w-auto md:max-w-[380px] md:translate-x-0 lg:max-w-[500px]">
            <p className="eyebrow fleet:text-text-heading-alt">
              {t("pdp.obd_compatibility_eyebrow")}
            </p>
            <SectionTitle className="text-text-heading whitespace-pre-line max-md:backdrop-blur-[3.55px]">
              {t("pdp.obd_compatibility_headline")}
            </SectionTitle>
            <p className="text-text-body-secondary md:text-figma-body-l-size md:text-text-body-alt md:fleet:text-text-body-secondary text-xs leading-[18px] max-md:mt-auto md:leading-[1.1]">
              <Trans
                t={t}
                i18nKey="pdp.obd_compatibility_description"
                components={{
                  trigger: (
                    <button
                      type="button"
                      onClick={() => setOpen(true)}
                      aria-label={t("pdp.obd_compatibility_cta")}
                      className="underline underline-offset-2"
                    />
                  ),
                }}
              />
            </p>
          </div>
        </div>
      </section>
    </>
  );
}
